/**
 * 预设数据服务
 * 统一管理材料、设备、植物数据的API调用
 */
import api from './api';

// 数据类型定义
export interface PresetMaterial {
  _id: string;
  materialName: string;
  defaultUnit: string;
  alternativeUnits?: string[];
  category: string;
  specifications?: string;
  description?: string;
  tags: string[];
  plantType?: string;
  plantSpecs?: {
    diameter?: number;
    crownWidth?: number;
    height?: number;
    rootBallSize?: number;
  };
  usageCount: number;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PresetEquipment {
  _id: string;
  equipmentName: string;
  equipmentModel?: string;
  category: string;
  unit: string;
  specifications?: string;
  description?: string;
  tags: string[];
  operatingSpecs?: {
    power?: number;
    capacity?: number;
    workingRadius?: number;
    maxHeight?: number;
    fuelConsumption?: number;
  };
  usageCount: number;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface DataStatistics {
  totalCount: number;
  activeCount: number;
  categoryCount: number;
  recentlyAdded: number;
  usageTotal: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export class PresetDataService {
  // ==================== 预设材料相关 ====================
  
  /**
   * 获取所有预设材料
   */
  static async getMaterials(params?: {
    category?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PresetMaterial[]>> {
    const response = await api.get('/preset-materials', { params });
    return response;
  }

  /**
   * 搜索预设材料
   */
  static async searchMaterials(query: string): Promise<ApiResponse<PresetMaterial[]>> {
    const response = await api.get('/preset-materials/search', {
      params: { q: query }
    });
    return response;
  }

  /**
   * 获取材料分类
   */
  static async getMaterialCategories(): Promise<ApiResponse<string[]>> {
    const response = await api.get('/preset-materials/categories');
    return response;
  }

  /**
   * 获取热门材料
   */
  static async getPopularMaterials(limit: number = 10): Promise<ApiResponse<PresetMaterial[]>> {
    const response = await api.get('/preset-materials/popular', {
      params: { limit }
    });
    return response;
  }

  /**
   * 创建预设材料
   */
  static async createMaterial(data: {
    materialName: string;
    defaultUnit: string;
    alternativeUnits?: string[];
    category: string;
    specifications?: string;
    description?: string;
    tags?: string[];
    createdBy: string;
  }): Promise<ApiResponse<PresetMaterial>> {
    const response = await api.post('/preset-materials', data);
    return response;
  }

  /**
   * 更新预设材料
   */
  static async updateMaterial(id: string, data: Partial<PresetMaterial>): Promise<ApiResponse<PresetMaterial>> {
    const response = await api.put(`/preset-materials/${id}`, data);
    return response;
  }

  /**
   * 激活/停用材料
   */
  static async toggleMaterialStatus(id: string, isActive: boolean): Promise<ApiResponse<PresetMaterial>> {
    const endpoint = isActive ? 'activate' : 'deactivate';
    const response = await api.put(`/preset-materials/${id}/${endpoint}`);
    return response;
  }

  // ==================== 预设设备相关 ====================

  /**
   * 获取所有预设设备
   */
  static async getEquipment(params?: {
    category?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PresetEquipment[]>> {
    const response = await api.get('/preset-equipment', { params });
    return response;
  }

  /**
   * 搜索预设设备
   */
  static async searchEquipment(query: string): Promise<ApiResponse<PresetEquipment[]>> {
    const response = await api.get('/preset-equipment/search', {
      params: { q: query }
    });
    return response;
  }

  /**
   * 获取设备分类
   */
  static async getEquipmentCategories(): Promise<ApiResponse<string[]>> {
    const response = await api.get('/preset-equipment/categories');
    return response;
  }

  /**
   * 获取热门设备
   */
  static async getPopularEquipment(limit: number = 10): Promise<ApiResponse<PresetEquipment[]>> {
    const response = await api.get('/preset-equipment/popular', {
      params: { limit }
    });
    return response;
  }

  /**
   * 创建预设设备
   */
  static async createEquipment(data: {
    equipmentName: string;
    equipmentModel?: string;
    category: string;
    unit: string;
    specifications?: string;
    description?: string;
    tags?: string[];
    operatingSpecs?: any;
    createdBy: string;
  }): Promise<ApiResponse<PresetEquipment>> {
    const response = await api.post('/preset-equipment', data);
    return response;
  }

  /**
   * 更新预设设备
   */
  static async updateEquipment(id: string, data: Partial<PresetEquipment>): Promise<ApiResponse<PresetEquipment>> {
    const response = await api.put(`/preset-equipment/${id}`, data);
    return response;
  }

  /**
   * 激活/停用设备
   */
  static async toggleEquipmentStatus(id: string, isActive: boolean): Promise<ApiResponse<PresetEquipment>> {
    const endpoint = isActive ? 'activate' : 'deactivate';
    const response = await api.put(`/preset-equipment/${id}/${endpoint}`);
    return response;
  }

  // ==================== 植物相关 ====================

  /**
   * 获取植物类型
   */
  static async getPlantTypes(): Promise<ApiResponse<string[]>> {
    const response = await api.get('/preset-materials/plant-types');
    return response;
  }

  /**
   * 按植物类型获取植物
   */
  static async getPlantsByType(plantType: string): Promise<ApiResponse<PresetMaterial[]>> {
    const response = await api.get(`/preset-materials/plants/${plantType}`);
    return response;
  }

  /**
   * 搜索植物
   */
  static async searchPlants(query: string): Promise<ApiResponse<PresetMaterial[]>> {
    const response = await api.get('/preset-materials/plants/search', {
      params: { q: query }
    });
    return response;
  }

  /**
   * 获取植物统计
   */
  static async getPlantStats(): Promise<ApiResponse<any>> {
    const response = await api.get('/preset-materials/plant-stats');
    return response;
  }

  /**
   * 获取热门植物
   */
  static async getPopularPlants(limit: number = 10): Promise<ApiResponse<PresetMaterial[]>> {
    const response = await api.get('/preset-materials/popular-plants', {
      params: { limit }
    });
    return response;
  }

  /**
   * 创建植物材料
   */
  static async createPlant(data: {
    materialName: string;
    defaultUnit: string;
    plantType: string;
    plantSpecs?: any;
    category: string;
    specifications?: string;
    description?: string;
    tags?: string[];
    createdBy: string;
  }): Promise<ApiResponse<PresetMaterial>> {
    const response = await api.post('/preset-materials/plants', data);
    return response;
  }

  // ==================== 统计相关 ====================

  /**
   * 计算数据统计
   */
  static calculateStatistics(data: (PresetMaterial | PresetEquipment)[]): DataStatistics {
    const totalCount = data.length;
    const activeCount = data.filter(item => item.isActive).length;
    const categoryCount = new Set(data.map(item => item.category)).size;
    
    // 计算最近7天新增的数据
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const recentlyAdded = data.filter(item => {
      const createdDate = new Date(item.createdAt);
      return createdDate > weekAgo;
    }).length;
    
    const usageTotal = data.reduce((sum, item) => sum + item.usageCount, 0);

    return {
      totalCount,
      activeCount,
      categoryCount,
      recentlyAdded,
      usageTotal
    };
  }

  /**
   * 获取所有植物数据（包含在材料中）
   */
  static async getAllPlants(): Promise<ApiResponse<PresetMaterial[]>> {
    const response = await api.get('/preset-materials', {
      params: { plantType: 'all' }
    });
    
    // 过滤出植物数据
    const allMaterials = response.data;
    const plants = allMaterials.filter((material: PresetMaterial) => material.plantType);
    
    return {
      success: true,
      data: plants
    };
  }

  // ==================== 智能填充和重复检测相关 ====================

  /**
   * 检测材料重复
   */
  static async checkMaterialDuplicate(
    materialName: string,
    options?: {
      threshold?: number;
      limit?: number;
    }
  ): Promise<ApiResponse<any>> {
    const response = await api.post('/preset-materials/check-duplicate', {
      materialName,
      ...options
    });
    return response;
  }

  /**
   * 检测设备重复
   */
  static async checkEquipmentDuplicate(
    equipmentName: string,
    equipmentModel?: string,
    options?: {
      threshold?: number;
      limit?: number;
    }
  ): Promise<ApiResponse<any>> {
    const response = await api.post('/preset-equipment/check-duplicate', {
      equipmentName,
      equipmentModel,
      ...options
    });
    return response;
  }

  /**
   * 获取材料智能填充建议
   */
  static async getMaterialAutoFillSuggestions(
    materialName: string,
    category?: string,
    userId?: string
  ): Promise<ApiResponse<any>> {
    const response = await api.post('/preset-materials/auto-fill-suggestions', {
      materialName,
      category,
      userId
    });
    return response;
  }

  /**
   * 获取设备智能填充建议
   */
  static async getEquipmentAutoFillSuggestions(
    equipmentName: string,
    category?: string,
    userId?: string
  ): Promise<ApiResponse<any>> {
    const response = await api.post('/preset-equipment/auto-fill-suggestions', {
      equipmentName,
      category,
      userId
    });
    return response;
  }

  /**
   * 查找相似材料
   */
  static async findSimilarMaterials(
    materialName: string,
    limit: number = 5
  ): Promise<ApiResponse<any>> {
    const response = await api.get('/preset-materials/similar', {
      params: { name: materialName, limit }
    });
    return response;
  }

  /**
   * 查找相似设备
   */
  static async findSimilarEquipment(
    equipmentName: string,
    limit: number = 5
  ): Promise<ApiResponse<any>> {
    const response = await api.get('/preset-equipment/similar', {
      params: { name: equipmentName, limit }
    });
    return response;
  }
}